import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../components/common/Icon";
import styles from "./ComposerSelect.module.css";

export interface ComposerSelectOption {
    id: string;
    name: string;
}

interface ComposerSelectProps {
    /** Sprite name used for the pill lead and the option rows. */
    icon: string;
    label: string;
    /** Pill value shown while nothing is selected. */
    placeholder: string;
    options: ComposerSelectOption[];
    value: string | null;
    onChange: (id: string | null) => void;
    /** When set, a first row clears the selection (referential selectors). */
    noneLabel?: string;
    /** When set, a last row links to the referential management screen. */
    manageLabel?: string;
    onManage?: () => void;
    /** "tool" keeps the discreet look of the language pill (remix-b). */
    variant?: "frame" | "tool";
    /** Adds a filter field at the top of the menu, for long lists (languages). */
    searchable?: boolean;
    searchPlaceholder?: string;
    /** Missing required value: the pill turns red. */
    invalid?: boolean;
    /** Fired when an open menu closes, whatever closed it: the pill's equivalent of a blur. */
    onClose?: () => void;
}

/** Case and accent insensitive, so "fr" finds "Français" and "espa" finds "Español". */
const normalize = (value: string) =>
    value
        .normalize("NFD")
        .replace(/\p{Diacritic}/gu, "")
        .toLowerCase()
        .trim();

/** Composer pill (remix-b) opening a styled dropdown, instead of an OS-rendered native select. */
const ComposerSelect: React.FC<ComposerSelectProps> = ({
    icon,
    label,
    placeholder,
    options,
    value,
    onChange,
    noneLabel,
    manageLabel,
    onManage,
    variant = "frame",
    searchable = false,
    searchPlaceholder,
    invalid = false,
    onClose,
}) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState("");
    const rootRef = useRef<HTMLDivElement>(null);

    const close = useCallback(() => {
        setIsOpen(false);
        setQuery("");
        onClose?.();
    }, [onClose]);

    useEffect(() => {
        if (!isOpen) return;

        const onPointerDown = (event: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
                close();
            }
        };
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") close();
        };

        document.addEventListener("mousedown", onPointerDown);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("mousedown", onPointerDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [isOpen, close]);

    const selected = options.find((option) => option.id === value) || null;
    const isApplied = variant === "frame" && !!selected;
    const normalizedQuery = searchable ? normalize(query) : "";
    const visibleOptions = normalizedQuery
        ? options.filter((option) => normalize(option.name).includes(normalizedQuery))
        : options;

    const handlePick = (id: string | null) => {
        onChange(id);
        close();
    };

    return (
        <div className={styles.select} ref={rootRef}>
            <button
                type="button"
                className={[
                    styles.pill,
                    variant === "tool" ? styles.pillTool : "",
                    isApplied ? styles.pillApplied : "",
                    invalid ? styles.pillInvalid : "",
                ]
                    .filter(Boolean)
                    .join(" ")}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                onClick={() => (isOpen ? close() : setIsOpen(true))}
            >
                <span className={styles.lead}>
                    <Icon name={icon} />
                    {label}
                </span>
                <span className={styles.value}>{selected ? selected.name : placeholder}</span>
                <Icon name="ic-chevron-down" className={styles.chevron} />
            </button>
            {isOpen && (
                <div className={styles.menu} role="menu">
                    {searchable && (
                        <input
                            type="text"
                            className={styles.search}
                            placeholder={searchPlaceholder}
                            value={query}
                            autoFocus
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && visibleOptions.length > 0) {
                                    e.preventDefault();
                                    handlePick(visibleOptions[0].id);
                                }
                            }}
                        />
                    )}
                    {noneLabel && !normalizedQuery && (
                        <button
                            type="button"
                            role="menuitem"
                            className={`${styles.option}${selected ? "" : ` ${styles.optionSelected}`}`}
                            onClick={() => handlePick(null)}
                        >
                            <Icon name="ic-x" />
                            {noneLabel}
                        </button>
                    )}
                    {normalizedQuery && visibleOptions.length === 0 && (
                        <span className={styles.empty}>{t("noOptions")}</span>
                    )}
                    {visibleOptions.map((option) => (
                        <button
                            key={option.id}
                            type="button"
                            role="menuitem"
                            className={`${styles.option}${option.id === value ? ` ${styles.optionSelected}` : ""}`}
                            onClick={() => handlePick(option.id)}
                        >
                            <Icon name={icon} />
                            {option.name}
                        </button>
                    ))}
                    {manageLabel && onManage && (
                        <>
                            <span className={styles.separator} />
                            <button
                                type="button"
                                role="menuitem"
                                className={`${styles.option} ${styles.optionManage}`}
                                onClick={() => {
                                    close();
                                    onManage();
                                }}
                            >
                                <Icon name="ic-plus" />
                                {manageLabel}
                            </button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default ComposerSelect;
